import React, { useEffect, useState, useRef } from 'react'
import { styled, FloraVariants, forwardRef } from '@grupoboticario/flora-react-system'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { Flex } from '@grupoboticario/flora-react-flex'
import { Text } from '@grupoboticario/flora-react-text'
import useObservable from './libs/useObservable'
import SliderContext, { useSlider } from './SliderContext'
import { motion } from 'framer-motion'

const linearScale = (input: readonly [number, number], output: readonly [number, number]) => {
  return (value: number) => {
    if (input[0] === input[1] || output[0] === output[1]) {
      return output[0]
    }

    const ratio = output[1] / input[1]
    return output[0] + ratio * (value - input[0])
  }
}

const getThumbInBoundsOffset = (width: number, left: number, direction: number) => {
  const halfWidth = width / 2
  const halfPercent = 50
  const offset = linearScale([0, halfPercent], [0, halfWidth])

  return (halfWidth - offset(left) * direction) * direction
}

const convertValueToPercentage = (value: number, min: number, max: number) => {
  const maxSteps = max - min
  const percentPerStep = 100 / maxSteps

  return percentPerStep * (value - min)
}

const scaleByPercent = (percent: number, max: number, min: number): number =>
  (percent / 100) * (max - min) + min

const StyledWrapper = styled(Flex, {
  userSelect: 'none',
  width: '100%',
  variants: {
    variant: {
      alt: {
        color: '$nonInteractiveAltPredominant',
      },
      standard: {
        color: '$nonInteractivePredominant',
      },
    },
  },
  defaultVariants: {
    variant: 'standard',
  },
})

const StyledSlider = styled(SliderPrimitive.Root, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  userSelect: 'none',
  touchAction: 'none',
  height: 16,
  width: '100%',
})

const StyledTrack = styled(SliderPrimitive.Track, {
  position: 'relative',
  flexGrow: 1,
  height: 4,
  overflow: 'hidden',
  borderRadius: '$large',
  variants: {
    variant: {
      alt: {
        backgroundColor: '$disabledAltBackground',
      },
      standard: {
        backgroundColor: '$disabledBackground',
      },
    },
  },
  defaultVariants: {
    variant: 'standard',
  },
})

const StyledRange = styled(SliderPrimitive.Range, {
  position: 'absolute',
  height: '100%',
  variants: {
    variant: {
      alt: {
        backgroundColor: '$actionableAlt',
      },
      standard: {
        backgroundColor: '$actionableDefault',
      },
    },
  },
  defaultVariants: {
    variant: 'standard',
  },
})

const StyledThumb = styled(SliderPrimitive.Thumb, {
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 32,
  height: 32,
  border: '1px solid',
  borderRadius: '20px',
  color: '$nonInteractivePredominant',
  fontSize: '$auxiliar',
  cursor: 'pointer',
  transition: 'width .1s ease-out, height .1s ease-out',
  '&:focus': {
    outline: 'none',
  },
  '& > span': {
    transition: 'opacity 0.12s ease-out',
    color: '$actionableContent',
    opacity: 1,
    '&[data-thumb-state="dragging"]': {
      opacity: 0,
    },
  },
  variants: {
    variant: {
      alt: {
        backgroundColor: '$actionableAlt',
        borderColor: '$actionableAlt',
        '& > span': {
          color: '$actionableAltContent',
        },
      },
      standard: {
        backgroundColor: '$actionableDefault',
        borderColor: '$actionableDefault',
        '& > span': {
          color: '$actionableContent',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'standard',
  },
})

const StyledBalloon = styled(motion.span, {
  width: 48,
  height: 56,
  position: 'absolute',
  fontSize: '$sm',
  opacity: 0,
  '& > svg path': {
    fill: '$actionableDefault',
  },
  variants: {
    variant: {
      alt: {
        color: '$actionableAltContent',
        '& > svg path': {
          fill: '$actionableAlt',
        },
      },
      standard: {
        color: '$actionableContent',
        '& > svg path': {
          fill: '$actionableDefault',
        },
      },
    },
  },
  '& > span': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fullscreen: 'absolute',
  },
})

const BalloonIcon = () => (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 56">
    <path d="M40.97 6.794c-9.372-9.059-24.568-9.059-33.94 0-9.373 9.059-9.373 23.745 0 32.804l.043.042L24 56l16.927-16.36.044-.042c9.372-9.058 9.372-23.745 0-32.804z" />
  </svg>
)
interface SliderThumbProps {
  position?: number
  variant?: 'alt' | 'standard'
  variation?: string
  trackElement?: HTMLSpanElement
  isDragging?: boolean
  currentThumb?: number | null
}

type SliderThumbOwnProps = FloraVariants<typeof StyledSlider> & SliderThumbProps

const customParser = (number: number) => Math.round(number * 100) / 100

function SliderThumb({
  position,
  isDragging = false,
  trackElement,
  currentThumb,
  ...props
}: SliderThumbOwnProps) {
  const thumb = useRef<HTMLSpanElement>(null)
  const thumbWidth = useRef<number>(0)
  const { currentValue, max, min, slideMove, slideStart, slideEnd } = useSlider()
  const lastPointerX = useRef<number>(0)
  const timestamp = useRef<number>(0)
  const currentThumbValue = position !== undefined ? currentValue[position] : 0

  thumbWidth.current = thumb?.current?.getBoundingClientRect()?.width || 0

  const finishRotationRef = useRef<number>()
  const [rotate, setRotate] = useState<number>(0)
  const [animationState, setAnimationState] = useState<string>('idle')

  const percent = convertValueToPercentage(currentThumbValue, min, max)

  const thumbInBoundsOffset = getThumbInBoundsOffset(thumbWidth.current, percent, 1)

  const scale = scaleByPercent(percent, 1.1, 0.84)
  const bottom = scaleByPercent(percent, 13, 5)
  const endSlide = useRef(false) // is used to finish balloon rotation
  const [startSlide, setStartSlide] = useState(false)

  const trackWidth = useRef(0)

  const finishRotate = () => {
    setRotate(0)
  }

  useEffect(() => {
    const getState = () => {
      if (startSlide) {
        return 'start'
      }

      if (endSlide.current) {
        return 'end'
      }

      if (isDragging) {
        return 'dragging'
      }

      return 'idle'
    }

    const state = getState()
    if (state !== animationState) {
      setAnimationState(state)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endSlide.current, startSlide, isDragging])

  useEffect(() => {
    if (trackElement && trackWidth.current === 0) {
      trackWidth.current = trackElement?.getBoundingClientRect()?.width
    }
  }, [trackElement])

  useEffect(() => {
    slideStart.subscribe(() => {
      endSlide.current = false
      setStartSlide(true)
    })

    slideEnd.subscribe(() => {
      endSlide.current = true
      setStartSlide(false)
    })

    slideMove.subscribe((thumbLeft = 0) => {
      endSlide.current = false
      setStartSlide(false)

      /* istanbul ignore else */
      if (!timestamp.current === null) {
        timestamp.current = Date.now()
        lastPointerX.current = thumbLeft
      }

      const diff = trackWidth.current * 0.02 // 2% of track width in px
      const now = Date.now()
      const averageSpeed = (thumbLeft - lastPointerX.current) / (now - timestamp.current) || 0
      let speedX = averageSpeed * 160

      if (speedX > diff) {
        speedX = diff
      } else if (speedX < -diff) {
        speedX = -diff
      }

      const force = -4
      speedX = speedX * force
      setRotate(speedX)

      timestamp.current = now
      lastPointerX.current = thumbLeft

      if (finishRotationRef.current) {
        cancelAnimationFrame(finishRotationRef.current)
      }

      finishRotationRef.current = requestAnimationFrame(finishRotate) as number
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const animationVariants = {
    dragging: {
      left: `calc(${percent}% + ${thumbInBoundsOffset}px) `,
      scale: customParser(scale),
      bottom: `calc(100% + ${bottom}px)`,
      opacity: [1, 1],
      rotate,
      transition: {
        rotate: {
          duration: 0.5,
          ease: 'easeOut',
        },
        left: {
          duration: 0.4,
          ease: 'easeOut',
        },
        scale: {
          duration: 0.4,
          ease: 'easeOut',
        },
        bottom: {
          duration: 0.4,
          ease: 'easeOut',
        },
      },
    },
    start: {
      left: [
        `calc(${percent}% + ${thumbInBoundsOffset}px) `,
        `calc(${percent}% + ${thumbInBoundsOffset}px) `,
      ],
      scale: [0, customParser(scale)],
      bottom: [`calc(100% + ${-25}px)`, `calc(100% + ${bottom}px)`],
      opacity: [0, 1],
      transition: {
        bottom: {
          duration: 0.2,
          delay: 0.1,
        },
        scale: {
          duration: 0.3,
        },
      },
    },
    end: {
      left: `calc(${percent}% + ${thumbInBoundsOffset}px) `,
      scale: [customParser(scale), 0],
      bottom: [`calc(100% + ${bottom}px)`, `calc(100% + ${-25}px)`],
      opacity: [1, 0],
      rotate: 0,
      transition: {
        bottom: {
          duration: 0.2,
        },
        scale: {
          duration: 0.3,
        },
      },
    },
    idle: {
      left: `calc(${percent}% + ${thumbInBoundsOffset}px) `,
      opacity: 0,
      bottom: `calc(100% + ${-25}px)`,
      scale: 0,
      rotate: 0,
    },
  }

  return (
    <>
      <StyledThumb {...props} ref={thumb}>
        <span aria-hidden="true" data-thumb-state={isDragging ? 'dragging' : ''}>
          {currentThumbValue}
        </span>
      </StyledThumb>
      <StyledBalloon
        data-balloon-state={isDragging ? 'open' : 'close'}
        aria-hidden="true"
        style={{
          translateX: '-50%',
        }}
        variants={animationVariants}
        animate={animationState}
        {...props}
      >
        <span aria-hidden="true">{currentThumbValue}</span>
        <BalloonIcon />
      </StyledBalloon>
    </>
  )
}

export type SliderProps = FloraVariants<typeof StyledWrapper> &
  FloraVariants<typeof StyledSlider> &
  SliderPrimitive.SliderProps & {
    name: string
    left?: string | React.ReactNode
    right?: string | React.ReactNode
    variant?: 'alt' | 'standard'
  }

/**
 * @component
 * @name Slider
 * @description Slider component
 * @tutorial https://flora.gb.tech/docs/components/core/slider
 */
export const Slider = forwardRef<'span', SliderProps>(({ left, right, ...props }, forwardRef) => {
  delete props.orientation

  const {
    max = 100,
    min = 0,
    defaultValue,
    value,
    onValueChange: callback = (values: number[]) => {},
  } = props
  const trackRef = useRef<HTMLSpanElement>()
  const sliderRef = useRef(forwardRef)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [currentValue, setCurrentValue] = useState<number[]>(value || defaultValue || [])
  const [currentThumb, setCurrentThumb] = useState<number | null>(null)
  const [currentThumbValue, setCurrentThumbValue] = useState<number | null>(null)

  const [allowEvents, setAllowEvents] = useState(false)

  const slideMove = useObservable()
  const slideStart = useObservable()
  const slideEnd = useObservable()

  useEffect(() => {
    setTimeout(() => {
      setAllowEvents(true)
    }, 800)
  }, [])

  const getDragX = (event: PointerEvent) => {
    const sliderOffset = (sliderRef.current as unknown as HTMLDivElement)?.offsetLeft
    const trackWidth = (trackRef.current as HTMLSpanElement)?.getBoundingClientRect()?.width
    const clientX = (event as PointerEvent).clientX

    let x = clientX - sliderOffset

    x = Math.max(x, 0)
    x = Math.min(x, trackWidth)

    return x
  }

  const onDragStart = (event: PointerEvent) => {
    setIsDragging(true)

    /* istanbul ignore else */
    currentValue.forEach((thumb, position) => {
      slideStart.emitter(position)
    })
  }

  const onDragEnd = (event?: PointerEvent) => {
    setIsDragging(false)

    currentValue.forEach((thumb, position) => {
      slideEnd.emitter(position)
    })
    setCurrentThumb(null)
  }

  const onDragMove = (event: PointerEvent) => {
    /* istanbul ignore else */
    if (isDragging) {
      slideMove.emitter(currentThumb, getDragX(event))
    }
  }

  return (
    <StyledWrapper
      variant={props.variant}
      css={{
        pointerEvents: allowEvents ? '' : 'none',
      }}
    >
      {left && (
        <Text
          size="exceptionsAuxiliarRegular"
          css={{
            marginRight: '$2',
          }}
        >
          {left}
        </Text>
      )}
      <SliderContext.Provider
        value={{
          isDragging,
          currentValue,
          max,
          min,
          slideMove,
          slideStart,
          slideEnd,
          currentThumbValue,
          ...props,
        }}
      >
        <StyledSlider
          onValueChange={(values: number[]) => {
            const changedIndex = values.findIndex(
              (eventValue: number, index: number) => eventValue !== (currentValue || [])[index],
            )

            /* istanbul ignore else */
            if (typeof callback === 'function') {
              callback(values)
            }
            setCurrentValue(values)
            setCurrentThumb(changedIndex)
            setCurrentThumbValue(values[changedIndex])
          }}
          onPointerDown={onDragStart}
          onPointerUp={onDragEnd}
          onPointerMove={onDragMove}
          data-flora="slider"
          variant={props.variant}
          {...props}
        >
          <StyledTrack
            variant={props.variant}
            ref={trackRef as React.MutableRefObject<HTMLSpanElement>}
          >
            <StyledRange variant={props.variant} />
          </StyledTrack>
          {currentValue.map((_: number, index: number) => {
            return (
              <SliderThumb
                position={index}
                isDragging={isDragging}
                key={index}
                currentThumb={currentThumb}
                trackElement={trackRef.current}
                variant={props.variant}
              />
            )
          })}
        </StyledSlider>
      </SliderContext.Provider>
      {right && (
        <Text
          size="exceptionsAuxiliarRegular"
          css={{
            marginLeft: '$2',
          }}
        >
          {right}
        </Text>
      )}
    </StyledWrapper>
  )
})
